import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from './NotificationContext';
import websocketService from '../services/websocketService';
import { chatApi } from '../services/chatApi';
import webrtcService from '../services/webrtcService';
import callSounds from '../services/callSounds';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({});
  const [recordingUsers, setRecordingUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // Track initial app load only
  const [hasConversationsLoaded, setHasConversationsLoaded] = useState(false); // Track if conversations have ever been loaded
  const [totalUnread, setTotalUnread] = useState(0);
  const [jumpToMessageId, setJumpToMessageId] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connected'); // 'connected', 'disconnected'
  const [networkError, setNetworkError] = useState(null);
  const typingTimeoutsRef = useRef({});
  const callTimeoutRefs = useRef([]);
  // Ref to track incomingCall without stale closure — updated synchronously so handlers
  // can always check the current value (avoids React 18 auto-batching staleness issues).
  const incomingCallRef = useRef(null);
  // Ref to track peers currently being reconnected via resumeCall.
  // handleCallParticipantLeft ignores these peers to avoid destroying freshly-created PCs.
  const reconnectingPeersRef = useRef(new Set());

  // --- Call state (Phase 2) ---
  // callState: 'idle' | 'calling' | 'incoming' | 'connecting' | 'active' | 'ended'
  const [callState, setCallState] = useState('idle');
  const [activeCall, setActiveCall] = useState(null);   // { callId, conversationId, initiatorId, participants, isGroup }
  const [incomingCall, setIncomingCall] = useState(null); // { callId, conversationId, initiatorId, initiatorName, isGroup }
  const [isMuted, setIsMuted] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({}); // peerId → MediaStream
  const [remoteMuteStates, setRemoteMuteStates] = useState({}); // peerId → muted bool
  const [participantStates, setParticipantStates] = useState({}); // peerId → connectionState string
  const [callStartTime, setCallStartTime] = useState(null); // timestamp set on first call_participant_connected
  const [speakingPeers, setSpeakingPeers] = useState({}); // peerId → boolean

  // Sync activeCall.callId to websocketService for reconnect resume
  useEffect(() => {
    websocketService.setActiveCallId(activeCall?.callId || null);
  }, [activeCall]);

  // Load conversations
  const loadConversations = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      }
      setNetworkError(null);
      setConnectionStatus('connected');
      const response = await chatApi.getConversations();
      if (response.success) {
        const convs = response.data || [];
        setConversations(convs);
        setHasConversationsLoaded(true); // Mark that we've loaded conversations at least once
        // Calculate total unread
        const unread = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setTotalUnread(unread);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setNetworkError(error.message || 'Network error');
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
      setInitialLoading(false); // Mark initial load as complete
    }
  }, []);

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId, limit = 50, offset = 0) => {
    try {
      const response = await chatApi.getMessages(conversationId, limit, offset);
      if (response.success) {
        const msgs = response.data || [];
        if (offset === 0) {
          setMessages(prev => ({ ...prev, [conversationId]: msgs }));
        } else {
          setMessages(prev => ({
            ...prev,
            [conversationId]: [...(prev[conversationId] || []), ...msgs],
          }));
        }
        return msgs;
      }
      return [];
    } catch (error) {
      console.error('Failed to load messages:', error);
      return [];
    }
  }, []);

  // Get message count for a conversation
  const getMessageCount = useCallback(async (conversationId) => {
    try {
      const response = await chatApi.getMessageCount(conversationId);
      if (response.success) {
        return response.data.count || 0;
      }
      return 0;
    } catch (error) {
      console.error('Failed to get message count:', error);
      return 0;
    }
  }, []);

  // Get a single message by ID
  const getMessage = useCallback(async (messageId) => {
    try {
      const response = await chatApi.getMessage(messageId);
      if (response.success) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to get message:', error);
      return null;
    }
  }, []);

  // Load messages before a given timestamp (for lazy loading older messages)
  const loadMessagesBefore = useCallback(async (conversationId, beforeTime, limit = 50) => {
    try {
      const response = await chatApi.getMessagesBefore(conversationId, beforeTime, limit);
      if (response.success) {
        const msgs = response.data || [];
        // Deduplicate and sort by date descending
        setMessages(prev => {
          const existing = prev[conversationId] || [];
          const existingIds = new Set(existing.map(m => m.id));
          const newMsgs = msgs.filter(m => !existingIds.has(m.id));
          // Combine and sort by date descending
          const allMessages = [...existing, ...newMsgs];
          const sortedMessages = allMessages.sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );
          return {
            ...prev,
            [conversationId]: sortedMessages,
          };
        });
        return msgs;
      }
      return [];
    } catch (error) {
      console.error('Failed to load messages before:', error);
      return [];
    }
  }, []);

  // Load messages after a given timestamp (for filling gaps when scrolling down)
  const loadMessagesAfter = useCallback(async (conversationId, afterTime, limit = 50) => {
    try {
      const response = await chatApi.getMessagesAfter(conversationId, afterTime, limit);
      if (response.success) {
        const msgs = response.data || [];
        // Deduplicate and sort by date descending
        setMessages(prev => {
          const existing = prev[conversationId] || [];
          const existingIds = new Set(existing.map(m => m.id));
          const newMsgs = msgs.filter(m => !existingIds.has(m.id));
          // Combine and sort by date descending
          const allMessages = [...existing, ...newMsgs];
          const sortedMessages = allMessages.sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );
          return {
            ...prev,
            [conversationId]: sortedMessages,
          };
        });
        return msgs;
      }
      return [];
    } catch (error) {
      console.error('Failed to load messages after:', error);
      return [];
    }
  }, []);

  // Load messages between two timestamps (for filling gaps when scrolling down)
  const loadMessagesBetween = useCallback(async (conversationId, afterTime, beforeTime, limit = 50) => {
    try {
      const response = await chatApi.getMessagesBetween(conversationId, afterTime, beforeTime, limit);
      if (response.success) {
        const msgs = response.data || [];
        // Deduplicate and ALWAYS sort by date descending to maintain correct order
        setMessages(prev => {
          const existing = prev[conversationId] || [];
          const existingIds = new Set(existing.map(m => m.id));
          const newMsgs = msgs.filter(m => !existingIds.has(m.id));

          // Combine all messages and sort by created_at descending (newest first)
          const allMessages = [...existing, ...newMsgs];
          const sortedMessages = allMessages.sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );

          return {
            ...prev,
            [conversationId]: sortedMessages,
          };
        });
        return msgs;
      }
      return [];
    } catch (error) {
      console.error('Failed to load messages between:', error);
      return [];
    }
  }, []);

  // Load messages around a specific message (for jumping to a message from search)
  // This replaces the current messages with a window around the target message
  const loadMessagesAround = useCallback(async (conversationId, messageId, beforeCount = 20, afterCount = 20) => {
    try {
      const response = await chatApi.getMessagesAround(conversationId, messageId, beforeCount, afterCount);
      if (response.success) {
        const msgs = response.data || [];
        // Replace messages with the window around the target
        // Sort by created_at descending (newest first) like other load functions
        const sortedMessages = msgs.sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
        setMessages(prev => ({
          ...prev,
          [conversationId]: sortedMessages,
        }));
        return { messages: sortedMessages, targetMessageId: messageId };
      }
      return null;
    } catch (error) {
      console.error('Failed to load messages around:', error);
      return null;
    }
  }, []);

  // Generate a temporary ID for optimistic messages
  const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Helper to create preview URL for a file
  const createPreviewUrl = (file) => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    } else if (file.type.startsWith('video/')) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  // Helper to determine media type
  const getMediaType = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'unknown';
  };

  // Send a message (with optional media files and voice blob) - optimistic UI
  const sendMessage = useCallback(async (conversationId, content, replyToMessageId = null, files = [], voiceBlob = null) => {
    // Ensure files is always an array
    const safeFiles = Array.isArray(files) ? files : [];
    const hasFiles = safeFiles.length > 0;
    const hasVoice = !!voiceBlob;

    // Create optimistic message with preview URLs for media
    const tempId = generateTempId();

    // Create preview URLs for files
    const previewUrls = hasFiles ? safeFiles.map(f => createPreviewUrl(f)) : [];
    const mediaTypes = hasFiles ? safeFiles.map(f => getMediaType(f)) : [];

    // Determine primary media type for push notification
    let mediaType = '';
    if (hasVoice) {
      mediaType = 'audio';
    } else if (hasFiles && mediaTypes.length > 0) {
      mediaType = mediaTypes[0]; // Use first file's type
    }

    // Create voice preview URL if voice blob exists
    const voicePreviewUrl = voiceBlob ? URL.createObjectURL(voiceBlob) : null;

    const optimisticMessage = {
      id: tempId,
      conversation_id: conversationId,
      content: content || '',
      reply_to_message_id: replyToMessageId,
      user_id: user.id,
      user: user,
      status: 'sending',
      created_at: new Date().toISOString(),
      // Media files - use preview URLs initially
      media_urls: hasFiles ? previewUrls : null,
      media_types: hasFiles ? mediaTypes : null,
      // Store original files for retry
      _mediaFiles: hasFiles ? safeFiles : null,
      // Voice
      voice_url: hasVoice ? voicePreviewUrl : null,
      voice_duration: hasVoice ? 0 : null,
      _voiceBlob: hasVoice ? voiceBlob : null,
    };

    // Immediately add message to state
    setMessages(prev => {
      const currentMessages = prev[conversationId] || [];
      return {
        ...prev,
        [conversationId]: [...currentMessages, optimisticMessage],
      };
    });

    // Update conversation's last_message optimistically
    setConversations(prev => {
      const updated = prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, last_message: optimisticMessage, updated_at: optimisticMessage.created_at }
          : conv
      );
      return updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    });

    // Helper to update message in state
    const updateMessage = (tempMessageId, updates) => {
      setMessages(prev => {
        const currentMessages = prev[conversationId] || [];
        return {
          ...prev,
          [conversationId]: currentMessages.map(msg =>
            msg.id === tempMessageId
              ? { ...msg, ...updates }
              : msg
          ),
        };
      });
    };

    // Helper to update message status
    const updateMessageStatus = (tempMessageId, newStatus, serverMessage = null) => {
      updateMessage(tempMessageId, { ...serverMessage, status: newStatus });
    };

    try {
      // Send the message via API
      const response = await chatApi.sendMessage({
        conversation_id: conversationId,
        content: content || '',
        reply_to_message_id: replyToMessageId,
        media_type: mediaType || undefined, // Include media type for push notification
      });

      if (!response.success) {
        updateMessageStatus(tempId, 'failed');
        throw new Error(response.error || 'Failed to send message');
      }

      let finalMessage = response.data;
      const messageId = response.data.id;

      // Upload media files sequentially and get updated message
      for (let i = 0; i < safeFiles.length; i++) {
        const file = safeFiles[i];
        // Update progress
        updateMessage(tempId, { uploadProgress: Math.round(((i + 0.5) / safeFiles.length) * 100) });

        const mediaResponse = await chatApi.uploadMessageMedia(messageId, file);
        if (mediaResponse.success && mediaResponse.data) {
          finalMessage = mediaResponse.data;
          // Update with actual URL
          updateMessage(tempId, {
            media_urls: finalMessage.media_urls,
            uploadProgress: Math.round(((i + 1) / safeFiles.length) * 100)
          });
        }
      }

      // Upload voice and get updated message
      if (voiceBlob) {
        updateMessage(tempId, { uploadProgress: 90 });
        const voiceResponse = await chatApi.uploadMessageVoice(messageId, voiceBlob);
        if (voiceResponse.success && voiceResponse.data) {
          finalMessage = voiceResponse.data;
          updateMessage(tempId, {
            voice_url: finalMessage.voice_url,
            voice_duration: finalMessage.voice_duration,
            uploadProgress: 100
          });
        }
      }

      // Update to sent status with server message data
      // Clean up preview URLs
      previewUrls.forEach(url => url && URL.revokeObjectURL(url));
      if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);

      updateMessageStatus(tempId, 'sent', {
        ...finalMessage,
        _mediaFiles: null,
        _voiceBlob: null,
      });

      // Update conversation's last_message with final message
      setConversations(prev => {
        const updated = prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, last_message: finalMessage, updated_at: finalMessage.created_at }
            : conv
        );
        return updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      });

      return finalMessage;
    } catch (error) {
      console.error('Failed to send message:', error);
      // Message status already set to 'failed' above
      throw error;
    }
  }, [user]);

  // Retry sending a failed message
  const retryMessage = useCallback(async (conversationId, messageId) => {
    // Find the message
    const message = messages[conversationId]?.find(m => m.id === messageId);
    if (!message) return;

    // Get files and voice blob for retry
    const files = message._mediaFiles || [];
    const voiceBlob = message._voiceBlob || null;

    // Determine media type for push notification
    let mediaType = '';
    if (voiceBlob) {
      mediaType = 'audio';
    } else if (files.length > 0 && message.media_types && message.media_types.length > 0) {
      mediaType = message.media_types[0];
    }

    // Set to sending
    setMessages(prev => ({
      ...prev,
      [conversationId]: prev[conversationId].map(m =>
        m.id === messageId ? { ...m, status: 'sending', uploadProgress: 0 } : m
      ),
    }));

    try {
      const response = await chatApi.sendMessage({
        conversation_id: conversationId,
        content: message.content,
        reply_to_message_id: message.reply_to_message_id,
        media_type: mediaType || undefined,
      });

      if (!response.success) {
        setMessages(prev => ({
          ...prev,
          [conversationId]: prev[conversationId].map(m =>
            m.id === messageId ? { ...m, status: 'failed' } : m
          ),
        }));
        throw new Error(response.error || 'Failed to send message');
      }

      let finalMessage = response.data;
      const messageIdServer = response.data.id;

      // Upload media files if any
      for (let i = 0; i < files.length; i++) {
        setMessages(prev => ({
          ...prev,
          [conversationId]: prev[conversationId].map(m =>
            m.id === messageId ? { ...m, uploadProgress: Math.round(((i + 0.5) / Math.max(files.length, 1)) * 100) } : m
          ),
        }));

        const mediaResponse = await chatApi.uploadMessageMedia(messageIdServer, files[i]);
        if (mediaResponse.success && mediaResponse.data) {
          finalMessage = mediaResponse.data;
        }
      }

      // Upload voice if any
      if (voiceBlob) {
        setMessages(prev => ({
          ...prev,
          [conversationId]: prev[conversationId].map(m =>
            m.id === messageId ? { ...m, uploadProgress: 90 } : m
          ),
        }));

        const voiceResponse = await chatApi.uploadMessageVoice(messageIdServer, voiceBlob);
        if (voiceResponse.success && voiceResponse.data) {
          finalMessage = voiceResponse.data;
        }
      }

      // Update to sent
      setMessages(prev => ({
        ...prev,
        [conversationId]: prev[conversationId].map(m =>
          m.id === messageId ? { ...m, ...finalMessage, status: 'sent', _mediaFiles: null, _voiceBlob: null, uploadProgress: 100 } : m
        ),
      }));
    } catch (error) {
      setMessages(prev => ({
        ...prev,
        [conversationId]: prev[conversationId].map(m =>
          m.id === messageId ? { ...m, status: 'failed' } : m
        ),
      }));
    }
  }, [messages]);

  // Delete a failed optimistic message (local only, no API call)
  const deleteFailedMessage = useCallback((conversationId, messageId) => {
    setMessages(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).filter(m => m.id !== messageId),
    }));
  }, []);

  // Create a conversation
  const createConversation = useCallback(async (participantIds, isGroup = false, name = '') => {
    const response = await chatApi.createConversation({
      participant_ids: participantIds,
      is_group: isGroup,
      name,
    });
    if (response.success) {
      // Add to conversations list if not already present
      setConversations(prev => {
        const exists = prev.find(c => c.id === response.data.id);
        if (exists) return prev;
        return [response.data, ...prev];
      });
      return response.data;
    }
    throw new Error(response.error || 'Failed to create conversation');
  }, []);

  // Mark messages as read
  const markAsRead = useCallback(async (conversationId, messageIds) => {
    if (!messageIds || messageIds.length === 0) return;
    try {
      await chatApi.markMessagesAsRead(conversationId, messageIds);
      // Update local unread count
      setConversations(prev =>
        prev.map(c => c.id === conversationId ? { ...c, unread_count: 0 } : c)
      );
      // Recalculate total
      setConversations(prev => {
        const unread = prev.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setTotalUnread(unread);
        return prev;
      });
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  }, []);

  // Edit a message
  const editMessage = useCallback(async (messageId, content) => {
    const response = await chatApi.updateMessage(messageId, content);
    if (response.success) return response.data;
    throw new Error(response.error || 'Failed to edit message');
  }, []);

  // Delete a message
  const deleteMessage = useCallback(async (messageId) => {
    const response = await chatApi.deleteMessage(messageId);
    if (response.success) return true;
    throw new Error(response.error || 'Failed to delete message');
  }, []);

  // Add reaction to a message
  const addReaction = useCallback(async (messageId, reaction) => {
    const response = await chatApi.addReaction(messageId, reaction);
    if (response.success) return response.data;
    throw new Error(response.error || 'Failed to add reaction');
  }, []);

  // Remove reaction from a message
  const removeReaction = useCallback(async (messageId, reaction) => {
    const response = await chatApi.removeReaction(messageId, reaction);
    if (response.success) return response.data;
    throw new Error(response.error || 'Failed to remove reaction');
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((conversationId) => {
    websocketService.send('typing', { conversation_id: conversationId });
  }, []);

  const sendStopTyping = useCallback((conversationId) => {
    websocketService.send('stop_typing', { conversation_id: conversationId });
  }, []);

  // Send recording indicator
  const sendRecording = useCallback((conversationId) => {
    websocketService.send('recording', { conversation_id: conversationId });
  }, []);

  const sendStopRecording = useCallback((conversationId) => {
    websocketService.send('stop_recording', { conversation_id: conversationId });
  }, []);

  // --- WebSocket event handlers ---

  const handleNewMessage = useCallback((message) => {
    // Add to messages list
    setMessages(prev => ({
      ...prev,
      [message.conversation_id]: [message, ...(prev[message.conversation_id] || [])],
    }));

    // Update conversation list: last message + reorder
    setConversations(prev => {
      const updated = prev.map(conv =>
        conv.id === message.conversation_id
          ? {
              ...conv,
              last_message: message,
              updated_at: message.created_at,
              unread_count: conv.id === activeConversationId
                ? conv.unread_count
                : (conv.unread_count || 0) + 1,
            }
          : conv
      );
      updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

      // Recalculate total unread
      const unread = updated.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setTotalUnread(unread);

      return updated;
    });
  }, [activeConversationId]);

  const handleMessageEdited = useCallback((message) => {
    setMessages(prev => ({
      ...prev,
      [message.conversation_id]: (prev[message.conversation_id] || []).map(m =>
        m.id === message.id ? message : m
      ),
    }));
  }, []);

  const handleMessageDeleted = useCallback(({ message_id, conversation_id }) => {
    setMessages(prev => ({
      ...prev,
      [conversation_id]: (prev[conversation_id] || []).map(m =>
        m.id === message_id ? { ...m, is_deleted: true, content: null } : m
      ),
    }));
  }, []);

  const handleReactionAdded = useCallback(({ message_id, reactions, conversation_id }) => {
    setMessages(prev => ({
      ...prev,
      [conversation_id]: (prev[conversation_id] || []).map(m =>
        m.id === message_id ? { ...m, reactions } : m
      ),
    }));
  }, []);

  const handleReactionRemoved = useCallback(({ message_id, reactions, conversation_id }) => {
    setMessages(prev => ({
      ...prev,
      [conversation_id]: (prev[conversation_id] || []).map(m =>
        m.id === message_id ? { ...m, reactions } : m
      ),
    }));
  }, []);

  const handleMessageRead = useCallback(({ message_ids, conversation_id, user_id }) => {
    if (!message_ids || !Array.isArray(message_ids)) return;
    setMessages(prev => {
      const convMessages = prev[conversation_id];
      if (!convMessages) return prev;
      return {
        ...prev,
        [conversation_id]: convMessages.map(msg =>
          message_ids.includes(msg.id)
            ? { ...msg, read_by: [...new Set([...(msg.read_by || []), user_id])] }
            : msg
        ),
      };
    });
  }, []);

  const handleTyping = useCallback(({ conversation_id, user_id }) => {
    const key = `${conversation_id}::${user_id}`;
    setTypingUsers(prev => ({ ...prev, [key]: true }));

    // Clear previous timeout for this key
    if (typingTimeoutsRef.current[key]) {
      clearTimeout(typingTimeoutsRef.current[key]);
    }
    // Auto-clear after 3 seconds
    typingTimeoutsRef.current[key] = setTimeout(() => {
      setTypingUsers(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 3000);
  }, []);

  const handleStopTyping = useCallback(({ conversation_id, user_id }) => {
    const key = `${conversation_id}::${user_id}`;
    setTypingUsers(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (typingTimeoutsRef.current[key]) {
      clearTimeout(typingTimeoutsRef.current[key]);
      delete typingTimeoutsRef.current[key];
    }
  }, []);

  // Recording handlers
  const handleRecording = useCallback(({ conversation_id, user_id }) => {
    const key = `${conversation_id}::${user_id}`;
    setRecordingUsers(prev => ({ ...prev, [key]: true }));
  }, []);

  const handleStopRecording = useCallback(({ conversation_id, user_id }) => {
    const key = `${conversation_id}::${user_id}`;
    setRecordingUsers(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleUserOnline = useCallback(({ user_id }) => {
    setOnlineUsers(prev => new Set(prev).add(user_id));
  }, []);

  const handleUserOffline = useCallback(({ user_id }) => {
    setOnlineUsers(prev => {
      const next = new Set(prev);
      next.delete(user_id);
      return next;
    });
  }, []);

  const handleOnlineUsers = useCallback(({ user_ids }) => {
    setOnlineUsers(new Set(user_ids || []));
  }, []);

  // Handle real-time notifications
  const handleNotification = useCallback((notification) => {
    addNotification(notification);
  }, [addNotification]);

  // --- Call signaling handlers (Phase 2) ---

  const getMyId = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id || payload.sub;
    } catch {
      return null;
    }
  }, []);

  const handleCallOutgoing = useCallback((data) => {
    // { call_id, timestamp } — server confirmed our call_init
    console.log('[Call] outgoing:', data);
    
    // Rename callMeta entry from temp callId to server-assigned callId
    // This is needed because startCall() uses a temp callId to initialize callMeta
    // before the server assigns the real callId
    const activeCallMeta = webrtcService.callMeta;
    // Find the temp callId entry (it would have been set with isInitiator: true)
    for (const [callId, meta] of activeCallMeta.entries()) {
      if (callId.startsWith('temp_') && meta.isInitiator) {
        console.log('[Call] outgoing: renaming callMeta from', callId, 'to', data.call_id);
        const metaData = activeCallMeta.get(callId);
        activeCallMeta.delete(callId);
        activeCallMeta.set(data.call_id, metaData);
        break;
      }
    }
    
    setActiveCall(prev => prev ? { ...prev, callId: data.call_id } : null);
  }, []);

  const handleCallRinging = useCallback((data) => {
    // { call_id, initiator_id, initiator_name, is_group, conversation_id, timestamp }
    // We are the callee — show incoming call UI
    const myId = getMyId();
    if (data.initiator_id === myId) return;
    const incomingData = {
      callId: data.call_id,
      conversationId: data.conversation_id,
      initiatorId: data.initiator_id,
      initiatorName: data.initiator_name,
      isGroup: data.is_group,
    };
    setIncomingCall(incomingData);
    incomingCallRef.current = incomingData;
    setCallState('incoming');
    callSounds.playRing();
  }, [getMyId]);

  const handleCallConnecting = useCallback((data) => {
    // { call_id, joined_by, timestamp }
    // Caller: at least one participant accepted — start WebRTC negotiation (Step 1)
    console.log('[TEST] handleCallConnecting received, callState:', callState, 'data:', JSON.stringify(data), 'activeCall.callId:', activeCall?.callId);
    
    // SYNC FIX: Use server timestamp if available for consistent start time across both peers
    // Server sends timestamp in RFC3339 format (e.g., "2026-05-06T12:47:00Z"), NOT Unix seconds
    // So we pass it directly to Date() - no multiplication needed
    const serverTimestamp = data.timestamp ? new Date(data.timestamp).getTime() : null;
    const startTime = serverTimestamp || Date.now();
    
    if (callState === 'calling') {
      console.log('[Call] transitioning to connecting state, calling onCallConnecting');
      // Start timer when call starts connecting (reliable point in call flow)
      if (!callStartTime) {
        console.log('[Call] handleCallConnecting: setting callStartTime for caller, serverTimestamp:', serverTimestamp);
        setCallStartTime(startTime);
        setActiveCall(prev => prev ? { ...prev, callStartTime: startTime } : prev);
      }
      setCallState('connecting');
      webrtcService.onCallConnecting(data.call_id, activeCall?.participants || []).catch(err => {
        console.error('[Call] onCallConnecting threw:', err.message);
      });
    } else if (callState === 'incoming') {
      // Bob (callee) accepted the call. Start WebRTC negotiation.
      // The callee creates answer (not offer) when receiving the offer from initiator.
      console.log('[Call] incoming state, transitioning to connecting, starting WebRTC');
      // Start timer when call starts connecting (reliable point in call flow)
      if (!callStartTime) {
        console.log('[Call] handleCallConnecting: setting callStartTime for callee, serverTimestamp:', serverTimestamp);
        setCallStartTime(startTime);
        setActiveCall(prev => prev ? { ...prev, callStartTime: startTime } : prev);
      }
      setCallState('connecting');
      webrtcService.onCallConnecting(data.call_id, activeCall?.participants || []).catch(err => {
        console.error('[Call] onCallConnecting (from incoming) threw:', err.message);
      });
    } else if (callState === 'connecting') {
      // Edge case: call_resume_state may have already set callState to 'connecting' during
      // reconnect, but WebRTC negotiation never started. Ensure it starts now.
      console.log('[Call] already connecting, calling onCallConnecting for WebRTC negotiation');
      webrtcService.onCallConnecting(data.call_id, activeCall?.participants || []).catch(err => {
        console.error('[Call] onCallConnecting threw:', err.message);
      });
    } else {
      console.log('[Call] handleCallConnecting: unexpected callState:', callState, 'ignoring');
    }
  }, [callState, activeCall, callStartTime]);

  const handleCallParticipantConnected = useCallback((data) => {
    // { call_id, peer_id, timestamp }
    // Start call timer on first participant connected (Step 5)
    if (!callStartTime) {
      setCallStartTime(Date.now());
      setActiveCall(prev => prev ? { ...prev, callStartTime: Date.now() } : prev);
      callSounds.playConnect();
    }
    setParticipantStates(prev => ({ ...prev, [data.peer_id]: 'connected' }));
  }, [callStartTime]);

  const handleCallParticipantLeft = useCallback((data) => {
    // { call_id, peer_id, reason, timestamp }
    // If we have an unanswered incoming call, the caller disconnected before we could accept.
    // Ignore this — call_ended will follow and we'll stay in 'incoming' state.
    if (incomingCallRef.current && incomingCallRef.current.callId === data.call_id) {
      return;
    }
    // If this peer is currently being reconnected (their PC is being rebuilt as part
    // of call_resume), ignore the stale participant_left — the new PC is being created.
    if (reconnectingPeersRef.current.has(data.peer_id)) {
      console.log('[Call] handleCallParticipantLeft: ignoring for reconnecting peer', data.peer_id);
      return;
    }
    webrtcService.destroyPeerConnection(data.peer_id);
    setRemoteStreams(prev => {
      const next = { ...prev };
      delete next[data.peer_id];
      return next;
    });
    setParticipantStates(prev => {
      const next = { ...prev };
      delete next[data.peer_id];
      return next;
    });
    setSpeakingPeers(prev => {
      const next = { ...prev };
      delete next[data.peer_id];
      return next;
    });
  }, []);

  const handleCallEnded = useCallback((data) => {
    // { call_id, ended_by, reason, initiator_id, timestamp }
    // If we have an unanswered incoming call with the same call_id, this is a stale
    // call_ended triggered by the caller's disconnect — ignore it. The incoming call
    // UI stays visible so the user can still accept or reject.
    console.log('[Call] handleCallEnded received, data:', JSON.stringify(data), 'current callId:', activeCall?.callId, 'incomingCall:', incomingCallRef.current?.callId);
    if (incomingCallRef.current && incomingCallRef.current.callId === data.call_id) {
      console.log('[Call] handleCallEnded: ignoring stale call_ended for incoming call');
      return;
    }
    console.log('[Call] handleCallEnded: processing, setting state to ended');
    webrtcService.endCall(data.call_id);
    setCallState('ended');
    setActiveCall(null);
    setIncomingCall(null);
    incomingCallRef.current = null;
    setRemoteStreams({});
    setRemoteMuteStates({});
    setParticipantStates({});
    setSpeakingPeers({});
    setCallStartTime(null);
    callSounds.playEnd();
    const tid = setTimeout(() => setCallState('idle'), 2500);
    callTimeoutRefs.current.push(tid);
  }, []);

  const handleCallMissed = useCallback((data) => {
    // { call_id, timestamp }
    webrtcService.endCall(data.call_id);
    setActiveCall(null);
    setCallState('idle');
    callSounds.playMissed();
  }, []);

  const handleCallRejected = useCallback((data) => {
    // { call_id, rejected_by, reason, timestamp }
    webrtcService.endCall(data.call_id);
    setActiveCall(null);
    setCallState('idle');
  }, []);

  const handleCallBusy = useCallback((data) => {
    // { conversation_id, busy_user_id, busy_user_name, timestamp }
    webrtcService.endCall(activeCall?.callId);
    setActiveCall(null);
    setCallState('idle');
  }, [activeCall]);

  const handleCallCancelled = useCallback((data) => {
    // { call_id, timestamp }
    callSounds.stop();
    webrtcService.endCall(data.call_id);
    setIncomingCall(null);
    incomingCallRef.current = null;
    setCallState('idle');
  }, []);

  const handleCallTimeout = useCallback((data) => {
    // { call_id, timestamp } — callee's ringing timed out
    callSounds.stop();
    setIncomingCall(null);
    incomingCallRef.current = null;
    setCallState('idle');
  }, []);

  const handleCallOffer = useCallback((data) => {
    // { call_id, peer_id, sdp }
    webrtcService.handleOffer(data.call_id, data.peer_id, data.sdp);
  }, []);

  const handleCallAnswer = useCallback((data) => {
    // { call_id, peer_id, sdp }
    webrtcService.handleAnswer(data.peer_id, data.sdp);
  }, []);

  const handleCallICE = useCallback((data) => {
    // { call_id, peer_id, candidate }
    webrtcService.handleICE(data.peer_id, data.candidate);
  }, []);

  const handleCallMuteState = useCallback((data) => {
    // { call_id, peer_id, muted, timestamp }
    setRemoteMuteStates(prev => ({ ...prev, [data.peer_id]: data.muted }));
  }, []);

  const handleCallResumeState = useCallback((data) => {
    // Ignore stale call_resume_state from a previous/orphaned session.
    // Only process if this resume is for the call we're currently in.
    if (activeCall?.callId && data.call_id !== activeCall.callId) {
      console.log('[Call] handleCallResumeState: ignoring stale resume for', data.call_id, '(current:', activeCall.callId, ')');
      return;
    }
    // { call_id, initiator_id, participants[], timestamp }
    console.log('[Call] handleCallResumeState received, data:', JSON.stringify(data), 'activeCall.callId:', activeCall?.callId, 'callState:', callState);
    // Mark all non-self participants as "being reconnected" so handleCallParticipantLeft
    // ignores any stale participant_left events that race with the new PC creation.
    const myId = (() => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.sub;
      } catch { return null; }
    })();
    const peerIds = (data.participants || [])
      .filter(p => p.id !== myId)
      .map(p => p.id);
    peerIds.forEach(id => reconnectingPeersRef.current.add(id));

    // Clear stale state from any previous call session before resuming
    // BUT first capture the original callStartTime for timer sync (before nulling it)
    const originalCallStartTime = callStartTime;
    setRemoteStreams({});
    setParticipantStates({});
    setSpeakingPeers({});
    setRemoteMuteStates({});
    setCallStartTime(null);

    // KEY FIX: Don't overwrite activeCall.participants if we're already in 'connecting' state.
    // This happens for the CALLEE (bob): acceptCall sets participants=[{id: alice, state: accepted}],
    // then WS flaps and call_resume_state arrives with participants=[alice, bob].
    // We should NOT overwrite with the server's participants because:
    // 1. The server's participants may not match what we need (missing our own ID)
    // 2. handleCallConnecting -> onCallConnecting needs our original participant list
    // Instead, only update participants on fresh resume (when activeCall.callId is null).
    const shouldPreserveParticipants = activeCall?.callId && callState === 'connecting';
    if (shouldPreserveParticipants) {
      console.log('[Call] handleCallResumeState: preserving existing participants (already in connecting state)');
      setActiveCall(prev => ({
        ...prev,
        callId: data.call_id,
        initiatorId: data.initiator_id,
        // Don't overwrite participants — keep what we have from acceptCall/call_connecting
      }));
    } else {
      console.log('[Call] handleCallResumeState: updating participants from server');
      setActiveCall(prev => ({
        ...prev,
        callId: data.call_id,
        initiatorId: data.initiator_id,
        participants: data.participants,
      }));
    }

    // Use the right participants list for resumeCall:
    // - If preserving, use activeCall.participants (from acceptCall)
    // - Otherwise, use server's data.participants
    const resumeParticipants = shouldPreserveParticipants
      ? activeCall?.participants || []
      : data.participants || [];
    console.log('[Call] handleCallResumeState: calling resumeCall with participants:', JSON.stringify(resumeParticipants));
    webrtcService.resumeCall(data.call_id, activeCall?.conversationId, resumeParticipants).then(() => {
      console.log('[Call] handleCallResumeState: resumeCall completed');
      // Unmark all peers — they're no longer being reconnected
      peerIds.forEach(id => reconnectingPeersRef.current.delete(id));
    }).catch(err => {
      console.error('[Call] handleCallResumeState: resumeCall threw:', err.message);
      peerIds.forEach(id => reconnectingPeersRef.current.delete(id));
    });
    // Re-apply local mute state after resume
    webrtcService.setMuted(isMuted);

    // KEY FIX: Handle the case where caller reconnects after callee accepted.
    // Check if any peer has state=Accepted (1) or Connected (2)
    const hasPeerAccepted = (data.participants || []).some(p => 
      p.id !== myId && (p.state === 1 || p.state === 2) // state=1 Accepted, state=2 Connected
    );
    
    if (activeCall?.callId && callState === 'calling' && hasPeerAccepted) {
      // Caller missed call_connecting but peer has accepted - transition directly to active
      console.log('[Call] handleCallResumeState: caller missed call_connecting but peer accepted, setting to active');
      setCallState('active');
      // KEY FIX: Set callStartTime for timer sync — use server timestamp for consistency
      // NOTE: callStartTime was nulled earlier in this function, so use !originalCallStartTime
      // to check if we need to set it
      const serverTimestamp = data.timestamp ? new Date(data.timestamp).getTime() : Date.now();
      const startTime = originalCallStartTime || serverTimestamp;
      console.log('[Call] handleCallResumeState: setting callStartTime, original:', originalCallStartTime, 'server:', serverTimestamp, 'startTime:', startTime);
      setCallStartTime(startTime);
      setActiveCall(prev => prev ? { ...prev, callStartTime: startTime } : prev);
      // Trigger onCallConnecting to rebuild WebRTC connections
      webrtcService.onCallConnecting(data.call_id, resumeParticipants).catch(err => {
        console.error('[Call] handleCallResumeState: onCallConnecting threw:', err.message);
      });
    } else if (activeCall?.callId && callState === 'calling' && !hasPeerAccepted) {
      // Still waiting for peer to accept - preserve 'calling' state
      console.log('[Call] handleCallResumeState: caller awaiting peer accept, preserving "calling"');
    } else if (!activeCall?.callId && callState === 'calling') {
      // No callId yet and in calling state - preserve to wait for call_outgoing
      console.log('[Call] handleCallResumeState: caller awaiting call_outgoing, NOT changing callState (preserving "calling")');
    } else if (!activeCall?.callId && callState !== 'idle') {
      // If we have an unanswered incoming call (incoming state), PRESERVE it
      if (callState === 'incoming') {
        console.log('[Call] handleCallResumeState: preserving incoming state (user must accept/reject first)');
        setActiveCall({
          callId: data.call_id,
          initiatorId: data.initiator_id,
          participants: data.participants,
        });
      } else {
        console.log('[Call] handleCallResumeState: no activeCall.callId, setting callState=connecting');
        setCallState('connecting');
      }
      // KEY FIX: Start timer using server timestamp when resuming call state
      // Priority: 1) originalCallStartTime (preserved from before null), 2) server timestamp, 3) current time
      // This ensures Alice's timer continues from where it left off
      if (!callStartTime) {
        const serverTimestamp = data.timestamp ? new Date(data.timestamp).getTime() : null;
        const startTime = originalCallStartTime || serverTimestamp || Date.now();
        console.log('[Call] handleCallResumeState: setting callStartTime, original:', originalCallStartTime, 'server:', serverTimestamp, 'startTime:', startTime);
        setCallStartTime(startTime);
        setActiveCall(prev => prev ? { ...prev, callStartTime: startTime } : prev);
      }
    } else {
      console.log('[Call] handleCallResumeState: activeCall.callId already set or state is idle, NOT changing callState');
    }
  }, [activeCall, isMuted, callState]);

  // Dispatch incoming call envelope to the appropriate handler
  const handleCallEnvelope = useCallback((envelope) => {
    const { event, data } = envelope;
    console.log('[Call] handleCallEnvelope event:', event, 'callState:', callState, 'activeCall:', activeCall?.callId);
    switch (event) {
      case 'call_outgoing': handleCallOutgoing(data); break;
      case 'call_ringing': handleCallRinging(data); break;
      case 'call_connecting': console.log('[TEST] dispatching call_connecting'); handleCallConnecting(data); break;
      case 'call_participant_connected': handleCallParticipantConnected(data); break;
      case 'call_participant_left': handleCallParticipantLeft(data); break;
      case 'call_ended': handleCallEnded(data); break;
      case 'call_missed': handleCallMissed(data); break;
      case 'call_rejected': handleCallRejected(data); break;
      case 'call_busy': handleCallBusy(data); break;
      case 'call_cancelled': handleCallCancelled(data); break;
      case 'call_timeout': handleCallTimeout(data); break;
      case 'call_offer': handleCallOffer(data); break;
      case 'call_answer': handleCallAnswer(data); break;
      case 'call_ice': handleCallICE(data); break;
      case 'call_mute_state': handleCallMuteState(data); break;
      case 'call_resume_state': handleCallResumeState(data); break;
      default: console.warn('[Call] unknown event:', event);
    }
  }, [handleCallOutgoing, handleCallRinging, handleCallConnecting, handleCallParticipantConnected,
      handleCallParticipantLeft, handleCallEnded, handleCallMissed, handleCallRejected,
      handleCallBusy, handleCallCancelled, handleCallTimeout, handleCallOffer, handleCallAnswer,
      handleCallICE, handleCallMuteState, handleCallResumeState]);

  // Wire webrtcService callbacks to ChatContext state
  useEffect(() => {
    webrtcService.onRemoteStream = (peerId, stream) => {
      setRemoteStreams(prev => ({ ...prev, [peerId]: stream }));
    };

    webrtcService.onConnectionStateChange = (peerId, state) => {
      setParticipantStates(prev => ({ ...prev, [peerId]: state }));
    };

    webrtcService.onCallConnected = (callId, peerId) => {
      // Send call_connected to server
      websocketService.send('call', {
        event: 'call_connected',
        data: { call_id: callId },
      });
      // Start call timer when first peer connects (if not already started by fallback timeout)
      if (!callStartTime) {
        setCallStartTime(Date.now());
        setActiveCall(prev => prev ? { ...prev, callStartTime: Date.now() } : prev);
      }
      // Move to active once we have at least one connected peer
      setCallState(prev => prev === 'connecting' ? 'active' : prev);
    };

    webrtcService.onSpeakingChange = (peerId, speaking) => {
      setSpeakingPeers(prev => ({ ...prev, [peerId]: speaking }));
    };

    webrtcService.onPeerDisconnected = (peerId) => {
      setParticipantStates(prev => ({ ...prev, [peerId]: 'reconnecting' }));
    };

    webrtcService.onCallFailed = (callId, peerId) => {
      console.log('[Call] connection failed for peer', peerId);
      setCallState('ended');
      setTimeout(() => setCallState('idle'), 2500);
    };

    return () => {
      webrtcService.onRemoteStream = null;
      webrtcService.onConnectionStateChange = null;
      webrtcService.onCallConnected = null;
      webrtcService.onSpeakingChange = null;
      webrtcService.onPeerDisconnected = null;
      webrtcService.onCallFailed = null;
    };
  }, []);

  // REMOVED: Fallback timeout that forced call to active was removed.
  // WebRTC should complete ICE connection naturally. If it fails, the call
  // should fail properly rather than fake success on both sides.

  // Clear any pending call timeouts on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      callTimeoutRefs.current.forEach(clearTimeout);
      callTimeoutRefs.current = [];
    };
  }, []);

  // Connect WebSocket and set up listeners when user is authenticated
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    websocketService.connect(token);
    loadConversations();

    const handleVisibility = () => webrtcService.handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibility);

    const unsubscribers = [
      websocketService.on('new_message', handleNewMessage),
      websocketService.on('message_edited', handleMessageEdited),
      websocketService.on('message_deleted', handleMessageDeleted),
      websocketService.on('message_reaction_added', handleReactionAdded),
      websocketService.on('message_reaction_removed', handleReactionRemoved),
      websocketService.on('message_read', handleMessageRead),
      websocketService.on('typing', handleTyping),
      websocketService.on('stop_typing', handleStopTyping),
      websocketService.on('recording', handleRecording),
      websocketService.on('stop_recording', handleStopRecording),
      websocketService.on('user_online', handleUserOnline),
      websocketService.on('user_offline', handleUserOffline),
      websocketService.on('online_users', handleOnlineUsers),
      websocketService.on('notification', handleNotification),
      websocketService.on('call', handleCallEnvelope),
    ];

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      unsubscribers.forEach(unsub => unsub());
      websocketService.disconnect();
    };
  }, [user, loadConversations, handleNewMessage, handleMessageEdited, handleMessageDeleted, handleReactionAdded, handleReactionRemoved, handleMessageRead, handleTyping, handleStopTyping, handleUserOnline, handleUserOffline, handleOnlineUsers, handleNotification, handleCallEnvelope]);

  // Helper: get typing users for a conversation
  const getTypingUsers = useCallback((conversationId) => {
    return Object.keys(typingUsers)
      .filter(key => key.startsWith(`${conversationId}::`) && typingUsers[key])
      .map(key => parseInt(key.split('::')[1]));
  }, [typingUsers]);

  // Helper: get recording users for a conversation
  const getRecordingUsers = useCallback((conversationId) => {
    return Object.keys(recordingUsers)
      .filter(key => key.startsWith(`${conversationId}::`) && recordingUsers[key])
      .map(key => parseInt(key.split('::')[1]));
  }, [recordingUsers]);

  /**
   * Start an outgoing call to a conversation.
   * Sends call_init; waits for call_outgoing to get server-assigned callId.
   */
  const startCall = useCallback(async (conversationId, participants) => {
    console.log('[Call] startCall called, conversationId:', conversationId, 'participants:', participants);
    // Group call mesh topology limit (Step 10)
    if (participants.length > 4) {
      console.warn('[Call] group call exceeds 4 participants — mesh topology limit');
    }

    try {
      console.log('[Call] setting callState to calling');
      setCallState('calling');
      setCallStartTime(null); // reset — timer starts on first connected participant
      setActiveCall({
        callId: null, // assigned by server in call_outgoing
        conversationId,
        initiatorId: getMyId(),
        participants,
        isGroup: participants.length > 2,
      });

      // Initialize WebRTC service with isInitiator=true
      // We don't have the server-assigned callId yet, but we need callMeta set
      // The callMeta will be updated when call_outgoing arrives
      const tempCallId = `temp_${Date.now()}`;
      await webrtcService.initiateCall(tempCallId, conversationId, participants);

      // Ensure WS is open and message is delivered before proceeding
      // KEY FIX: Use sendAndWait for mobile reliability - this ensures call_init
      // is actually sent over the network before we show "calling" state
      console.log('[Call] waiting for WS to open and sending call_init...');
      try {
        await websocketService.sendAndWait('call', {
          event: 'call_init',
          data: { conversation_id: conversationId, is_group: participants.length > 2 },
        }, 10000);
        console.log('[Call] call_init sent successfully');
      } catch (e) {
        console.error('[Call] Failed to send call_init:', e.message);
        setCallState('idle');
        setActiveCall(null);
        webrtcService.endCall(tempCallId);
        return;
      }
    } catch (err) {
      console.error('[Call] startCall failed:', err);
      setCallState('idle');
      setActiveCall(null);
    }
  }, [getMyId]);

  /**
   * Accept an incoming call.
   */
  const acceptCall = useCallback(async () => {
    if (!incomingCall) return;
    callSounds.stop();
    console.log('[Call] acceptCall called, incomingCall:', incomingCall);
    try {
      setCallState('connecting');
      console.log('[Call] acceptCall: set callState to connecting');

      // KEY FIX: Set activeCallId BEFORE joinCall triggers the WS effect reconnection.
      // This ensures that when WS reconnects, it sends call_resume and gets call_resume_state
      // which is needed for the call to proceed.
      console.log('[Call] acceptCall: setting activeCallId for WS reconnection');
      websocketService.setActiveCallId(incomingCall.callId);

      const participants = [{ id: incomingCall.initiatorId, state: 'accepted' }];
      console.log('[Call] acceptCall: calling joinCall');
      await webrtcService.joinCall(incomingCall.callId, incomingCall.conversationId, participants);
      console.log('[Call] acceptCall: joinCall returned');

      setActiveCall({
        callId: incomingCall.callId,
        conversationId: incomingCall.conversationId,
        initiatorId: incomingCall.initiatorId,
        participants,
        isGroup: incomingCall.isGroup,
      });
      console.log('[Call] acceptCall: activeCall set');

      // KEY FIX: Use sendAndWait to ensure call_accept is actually sent.
      // This waits for WS to connect and flushes the message before proceeding.
      console.log('[Call] acceptCall: sending call_accept with sendAndWait');
      try {
        await websocketService.sendAndWait('call', {
          event: 'call_accept',
          data: { call_id: incomingCall.callId },
        });
        console.log('[Call] acceptCall: call_accept sent successfully');
      } catch (err) {
        console.error('[Call] acceptCall: failed to send call_accept:', err.message);
      }

      setIncomingCall(null);
      incomingCallRef.current = null;
    } catch (err) {
      console.error('[Call] acceptCall failed:', err);
      setCallState('idle');
      setActiveCall(null);
    }
  }, [incomingCall]);

  /**
   * Reject an incoming call.
   */
  const rejectCall = useCallback((reason = 'busy') => {
    if (!incomingCall) return;
    callSounds.stop();
    websocketService.send('call', {
      event: 'call_reject',
      data: { call_id: incomingCall.callId, reason },
    });
    setIncomingCall(null);
    incomingCallRef.current = null;
    setCallState('idle');
  }, [incomingCall]);

  /**
   * End the active call.
   */
  const endCurrentCall = useCallback(() => {
    callSounds.stop();
    const callId = activeCall?.callId;
    if (callId) {
      websocketService.send('call', {
        event: 'call_end',
        data: { call_id: callId },
      });
      webrtcService.endCall(callId);
    }
    setCallState('ended');
    setActiveCall(null);
    setRemoteStreams({});
    setRemoteMuteStates({});
    setParticipantStates({});
    setSpeakingPeers({});
    setCallStartTime(null);

    const tid = setTimeout(() => setCallState('idle'), 1500);
    callTimeoutRefs.current.push(tid);
  }, [activeCall]);

  /**
   * Toggle local mute.
   */
  const toggleMute = useCallback(() => {
    const callId = activeCall?.callId;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    webrtcService.setMuted(newMuted);

    if (callId) {
      websocketService.send('call', {
        event: newMuted ? 'call_mute' : 'call_unmute',
        data: { call_id: callId },
      });
    }
  }, [isMuted, activeCall]);

  /**
   * Get remote stream for a peer (for attaching to audio element).
   */
  const getRemoteStream = useCallback((peerId) => {
    return remoteStreams[peerId] || null;
  }, [remoteStreams]);

  /**
   * Get all remote peer IDs in the active call.
   */
  const getCallPeerIds = useCallback(() => {
    if (!activeCall?.callId) return [];
    return webrtcService.getPeerIds(activeCall.callId);
  }, [activeCall]);

  return (
    <ChatContext.Provider
      value={{
        conversations,
        messages,
        setMessages,
        activeConversationId,
        setActiveConversationId,
        onlineUsers,
        totalUnread,
        loading,
        initialLoading,
        hasConversationsLoaded,
        jumpToMessageId,
        setJumpToMessageId,
        connectionStatus,
        networkError,
        loadConversations,
        loadMessages,
        getMessageCount,
        getMessage,
        loadMessagesBefore,
        loadMessagesAfter,
        loadMessagesBetween,
        loadMessagesAround,
        sendMessage,
        retryMessage,
        deleteFailedMessage,
        createConversation,
        markAsRead,
        editMessage,
        deleteMessage,
        addReaction,
        removeReaction,
        sendTyping,
        sendStopTyping,
        getTypingUsers,
        sendRecording,
        sendStopRecording,
        getRecordingUsers,
        // --- Call state and actions (Phase 2) ---
        callState,
        activeCall,
        incomingCall,
        isMuted,
        remoteStreams,
        remoteMuteStates,
        participantStates,
        speakingPeers,
        startCall,
        acceptCall,
        rejectCall,
        endCurrentCall,
        toggleMute,
        getRemoteStream,
        getCallPeerIds,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
